/**
 * AYU Sessions — Übungen & Sessions
 *
 * 4 Kategorien:
 * 1. Basis-Sessions (5) — kostenlos, voraufgenommene Sprachaufnahmen
 * 2. Personalisierte Live-Sessions (11) — Premium, LLM + TTS Audio
 * 3. Live-Gespräche (6) — Premium, Echtzeit-Voice mit ElevenLabs ConvAI
 * 4. Chat-Übungen (7) — Premium, interaktive Text-Übungen im Chat
 */
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDemo } from "@/contexts/DemoContext";
import { useLocation } from "wouter";
import {
  Wind,
  Brain,
  Sparkles,
  Play,
  Pause,
  Clock,
  Lock,
  Headphones,
  Flame,
  Moon,
  Sun,
  Crown,
  Check,
  Zap,
  X,
  Loader2,
  Volume2,
  VolumeX,
  Star,
  Eye,
  Activity,
  Waves,
  Hand,
  Compass,
  Leaf,
  Info,
  ArrowRight,
  ChevronLeft,
  FileText,
  Mic,
  CheckCircle2,
  Timer,
  Phone,
  MessageSquare,
  Heart,
  Shield,
  Target,
  Users,
  Search,
  BookOpen,
  Utensils,
  RotateCcw,
  SkipBack,
  SkipForward,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { ThumbsFeedback } from "@/components/app/ThumbsFeedback";
import { useI18n } from "@/contexts/I18nContext";

/* ═══════════════════════════════════════════════════════════
   Category Filters
   ═══════════════════════════════════════════════════════════ */
type CategoryFilter =
  | "all"
  | "basis"
  | "atem"
  | "entspannung"
  | "meditation"
  | "energetisch"
  | "intensiv"
  | "live"
  | "chat";

const CATEGORIES: {
  key: CategoryFilter;
  labelKey: string;
  icon: React.ReactNode;
}[] = [
  { key: "all", labelKey: "sessions.katAlle", icon: <Sparkles className="w-3.5 h-3.5" /> },
  { key: "basis", labelKey: "sessions.katBasis", icon: <Play className="w-3.5 h-3.5" /> },
  { key: "atem", labelKey: "sessions.katAtem", icon: <Wind className="w-3.5 h-3.5" /> },
  {
    key: "entspannung",
    labelKey: "sessions.katEntspannung",
    icon: <Moon className="w-3.5 h-3.5" />,
  },
  {
    key: "meditation",
    labelKey: "sessions.katMeditation",
    icon: <Brain className="w-3.5 h-3.5" />,
  },
  {
    key: "energetisch",
    labelKey: "sessions.katEnergetisch",
    icon: <Flame className="w-3.5 h-3.5" />,
  },
  { key: "intensiv", labelKey: "sessions.katIntensiv", icon: <Zap className="w-3.5 h-3.5" /> },
  {
    key: "live",
    labelKey: "sessions.liveGespraeche",
    icon: <Phone className="w-3.5 h-3.5" />,
  },
  {
    key: "chat",
    labelKey: "sessions.chatUebungen",
    icon: <MessageSquare className="w-3.5 h-3.5" />,
  },
];

/* ═══════════════════════════════════════════════════════════
   Basis Sessions (5) — Kostenlos, voraufgenommen
   ═══════════════════════════════════════════════════════════ */
interface BasisSession {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: React.ReactNode;
  category: "atem" | "entspannung" | "meditation" | "energetisch";
  audioUrl: string;
}

const BASIS_SESSIONS: BasisSession[] = [
  {
    id: "morgen-meditation",
    title: "Morgen-Meditation",
    description: "Sanfter Start in den Tag mit Achtsamkeit",
    duration: "8 Min",
    icon: <Sun className="w-5 h-5" />,
    category: "meditation",
    audioUrl:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/morgen-meditation_607bf4d8.mp3",
  },
  {
    id: "4-7-8-atem",
    title: "4-7-8 Atemübung",
    description: "Beruhigende Atemtechnik nach Dr. Weil",
    duration: "5 Min",
    icon: <Wind className="w-5 h-5" />,
    category: "atem",
    audioUrl:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/4-7-8-atem_784161df.mp3",
  },
  {
    id: "body-scan",
    title: "Body Scan",
    description: "Körperwahrnehmung von Kopf bis Fuß",
    duration: "12 Min",
    icon: <Eye className="w-5 h-5" />,
    category: "entspannung",
    audioUrl:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/body-scan_63ef3f94.mp3",
  },
  {
    id: "abend-reflexion",
    title: "Abend-Reflexion",
    description: "Den Tag loslassen und zur Ruhe kommen",
    duration: "7 Min",
    icon: <Moon className="w-5 h-5" />,
    category: "entspannung",
    audioUrl:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/abend-reflexion_746338d7.mp3",
  },
  {
    id: "muskelentspannung",
    title: "Muskelentspannung",
    description: "Progressive Relaxation nach Jacobson",
    duration: "10 Min",
    icon: <Sparkles className="w-5 h-5" />,
    category: "entspannung",
    audioUrl:
      "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/muskelentspannung_b11793aa.mp3",
  },
];

/* ═══════════════════════════════════════════════════════════
   Personalisierte Sessions — Premium, Live mit LLM + TTS
   ═══════════════════════════════════════════════════════════ */
interface PersonalizedSession {
  id: string;
  type: string;
  title: string;
  description: string;
  duration: string;
  icon: React.ReactNode;
  category: CategoryFilter;
}

const PERSONALIZED_SESSIONS: PersonalizedSession[] = [
  {
    id: "p-nadi-shodhana",
    type: "nadi-shodhana",
    title: "Wechselatmung",
    description: "Nadi Shodhana für innere Balance",
    duration: "5–10 Min",
    icon: <Wind className="w-5 h-5" />,
    category: "atem",
  },
  {
    id: "p-bhramari",
    type: "bhramari",
    title: "Bhramari (Summ-Atem)",
    description: "Beruhigendes Summen für tiefe Ruhe",
    duration: "3–10 Min",
    icon: <Waves className="w-5 h-5" />,
    category: "atem",
  },
  {
    id: "p-vagusnerv",
    type: "pranayama-basis",
    title: "Vagusnerv-Stimulation",
    description: "Nervensystem beruhigen und regulieren",
    duration: "3–10 Min",
    icon: <Zap className="w-5 h-5" />,
    category: "energetisch",
  },
  {
    id: "p-breathwork",
    type: "kapalabhati",
    title: "Breath Work (intensiv)",
    description: "Intensive Atemarbeit für emotionale Lösung",
    duration: "10–20 Min",
    icon: <Flame className="w-5 h-5" />,
    category: "intensiv",
  },
  {
    id: "p-koerperreise",
    type: "bodyscan-ayurveda",
    title: "Imaginäre Körperreise",
    description: "Innere Reise durch deinen Körper",
    duration: "10–15 Min",
    icon: <Compass className="w-5 h-5" />,
    category: "entspannung",
  },
  {
    id: "p-achtsamkeit",
    type: "dankbarkeit",
    title: "Achtsamkeitsmeditation",
    description: "Geführte Meditation für innere Ruhe",
    duration: "5–20 Min",
    icon: <Brain className="w-5 h-5" />,
    category: "meditation",
  },
  {
    id: "p-dosha-balance",
    type: "dosha-balance",
    title: "Phänotyp-Balance Meditation",
    description: "Harmonisierung deines AYU-Phänotyps",
    duration: "5–20 Min",
    icon: <Leaf className="w-5 h-5" />,
    category: "meditation",
  },
  {
    id: "p-yoga-nidra",
    type: "yoga-nidra",
    title: "Yoga Nidra",
    description: "Tiefenentspannung zwischen Wachen und Schlafen",
    duration: "10–20 Min",
    icon: <Moon className="w-5 h-5" />,
    category: "entspannung",
  },
  {
    id: "p-chakra",
    type: "chakra-meditation",
    title: "Chakra-Meditation",
    description: "Reise durch die 7 Energiezentren",
    duration: "10–20 Min",
    icon: <Sparkles className="w-5 h-5" />,
    category: "energetisch",
  },
  {
    id: "p-abhyanga",
    type: "abhyanga-anleitung",
    title: "Abhyanga Selbstmassage",
    description: "Ayurvedische Ölmassage-Anleitung",
    duration: "10–20 Min",
    icon: <Hand className="w-5 h-5" />,
    category: "entspannung",
  },
  {
    id: "p-marma",
    type: "marma-massage",
    title: "Marma-Punkt Massage",
    description: "Stimulation der wichtigsten Energiepunkte",
    duration: "5–15 Min",
    icon: <Activity className="w-5 h-5" />,
    category: "energetisch",
  },
];

/* ═══════════════════════════════════════════════════════════
   Live-Gespräche (6) — Echtzeit-Voice mit Annam
   ═══════════════════════════════════════════════════════════ */
interface LiveSession {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: React.ReactNode;
  isFree: boolean;
  /** Initial message to send to Annam to start the conversation context */
  contextPrompt: string;
}

const LIVE_SESSIONS: LiveSession[] = [
  {
    id: "live-dinacharya",
    title: "Dinacharya-Reflexion",
    description: "Reflektiere deinen Tag aus ayurvedischer Sicht",
    duration: "ca. 10 Min",
    icon: <Sun className="w-5 h-5" />,
    isFree: true,
    contextPrompt:
      "Ich möchte meine Tagesroutine (Dinacharya) reflektieren. Hilf mir, meinen Tag aus ayurvedischer Sicht zu betrachten — was lief gut, was könnte ich verbessern?",
  },
  {
    id: "live-coaching",
    title: "Ayurveda-Coaching",
    description: "Persönliches Coaching zu deinem Thema",
    duration: "ca. 15 Min",
    icon: <Heart className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Ich möchte ein persönliches Ayurveda-Coaching. Frage mich, was mich gerade beschäftigt, und begleite mich mit ayurvedischer Weisheit.",
  },
  {
    id: "live-dosha-stress",
    title: "Dosha-Balance bei Stress",
    description: "Soforthilfe bei Ungleichgewicht und Stress",
    duration: "ca. 15 Min",
    icon: <Shield className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Ich fühle mich gestresst und aus der Balance. Hilf mir, mein Dosha-Ungleichgewicht zu erkennen und gib mir sofort umsetzbare Tipps zur Beruhigung.",
  },
  {
    id: "live-vata",
    title: "Vata-Beruhigung",
    description: "Ängste und Unruhe ayurvedisch begleiten",
    duration: "ca. 10 Min",
    icon: <Wind className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Ich spüre innere Unruhe und Ängste — typische Vata-Symptome. Begleite mich mit beruhigenden Techniken und ayurvedischen Empfehlungen, um mein Vata zu beruhigen.",
  },
  {
    id: "live-sankalpa",
    title: "Sankalpa-Session",
    description: "Intention und Lebensziel setzen",
    duration: "ca. 15 Min",
    icon: <Target className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Ich möchte mein Sankalpa (meine tiefste Intention) finden oder schärfen. Führe mich durch einen Prozess, um mein Lebensziel klar zu formulieren.",
  },
  {
    id: "live-sattva",
    title: "Sattva-Gespräch",
    description: "Konflikte und Beziehungen klären",
    duration: "ca. 15 Min",
    icon: <Users className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Ich habe einen Konflikt oder eine Beziehungssituation, die mich belastet. Hilf mir, aus sattvischer Perspektive Klarheit zu finden und Lösungen zu entwickeln.",
  },
];

/* ═══════════════════════════════════════════════════════════
   Chat-Übungen (7) — Interaktive Text-Übungen im Chat
   ═══════════════════════════════════════════════════════════ */
interface ChatExercise {
  id: string;
  title: string;
  description: string;
  duration: string;
  icon: React.ReactNode;
  isFree: boolean;
  /** Initial message to send to Annam to start the exercise */
  contextPrompt: string;
}

const CHAT_EXERCISES: ChatExercise[] = [
  {
    id: "chat-dosha-check",
    title: "Phänotyp-Tages-Check",
    description: "Wie steht dein Dosha heute? Schnell-Check",
    duration: "3–5 Min",
    icon: <Search className="w-5 h-5" />,
    isFree: true,
    contextPrompt:
      "Starte einen schnellen Phänotyp-Tages-Check mit mir. Frage mich Schritt für Schritt nach meinem aktuellen Zustand (Verdauung, Energie, Stimmung, Körpergefühl) und sage mir, welches Dosha heute dominiert und was ich tun kann.",
  },
  {
    id: "chat-dankbarkeit",
    title: "Dankbarkeits-Ritual",
    description: "3 Dinge, für die du dankbar bist",
    duration: "3–5 Min",
    icon: <Heart className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Führe mich durch ein ayurvedisches Dankbarkeits-Ritual. Frage mich nach 3 Dingen, für die ich heute dankbar bin, und verbinde jedes mit einem ayurvedischen Prinzip (Sattva, Ojas, Prana).",
  },
  {
    id: "chat-dharma",
    title: "Dharma-Kompass",
    description: "Werte und Lebenszweck erkunden",
    duration: "10 Min",
    icon: <Compass className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Führe mich durch eine Dharma-Kompass-Übung. Hilf mir, meine tiefsten Werte und meinen Lebenszweck (Dharma) zu erkunden — Schritt für Schritt mit Reflexionsfragen.",
  },
  {
    id: "chat-kosha",
    title: "Kosha-Arbeit",
    description: "Die 5 Hüllen des Selbst erkunden",
    duration: "10–15 Min",
    icon: <Eye className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Führe mich durch eine Kosha-Übung. Gehe mit mir Schritt für Schritt durch die 5 Hüllen (Annamaya, Pranamaya, Manomaya, Vijnanamaya, Anandamaya) und hilf mir, jede Schicht bewusst wahrzunehmen.",
  },
  {
    id: "chat-prakriti",
    title: "Prakriti-Stärken",
    description: "Stärken deiner Konstitution entdecken",
    duration: "5–10 Min",
    icon: <Star className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Hilf mir, die Stärken meiner Prakriti (Grundkonstitution) zu entdecken. Basierend auf meinem Dosha-Profil — was sind meine natürlichen Talente, Stärken und Gaben? Führe mich interaktiv durch die Erkundung.",
  },
  {
    id: "chat-ernaehrung",
    title: "Sattvische Ernährung",
    description: "Ernährungsplan nach deinem Dosha",
    duration: "5–10 Min",
    icon: <Utensils className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Erstelle mit mir zusammen einen sattvischen Ernährungsplan. Frage mich nach meinen Vorlieben und Unverträglichkeiten und gib mir dann Dosha-gerechte Empfehlungen für Frühstück, Mittag und Abendessen.",
  },
  {
    id: "chat-ritucharya",
    title: "Ritucharya-Planer",
    description: "Saisonale Routinen für die aktuelle Jahreszeit",
    duration: "5–10 Min",
    icon: <RotateCcw className="w-5 h-5" />,
    isFree: false,
    contextPrompt:
      "Führe mich durch eine Ritucharya-Planung. Basierend auf der aktuellen Jahreszeit — welche Routinen, Ernährung und Aktivitäten empfiehlt Ayurveda? Erstelle einen konkreten Plan mit mir.",
  },
];

/* ═══════════════════════════════════════════════════════════
   Session Card — Clean design, no prices
   ═══════════════════════════════════════════════════════════ */
function SessionCard({
  title,
  description,
  duration,
  icon,
  isFree,
  isLocked,
  isGenerating,
  onAction,
}: {
  title: string;
  description: string;
  duration: string;
  icon: React.ReactNode;
  isFree: boolean;
  isLocked: boolean;
  isGenerating?: boolean;
  onAction: () => void;
}) {
  const { t } = useI18n();
  return (
    <div
      className={`group rounded-2xl border p-5 transition-all duration-200 cursor-pointer ${
        isLocked
          ? "bg-[#f0ede5]/60 border-[#e0dbd0] opacity-55 grayscale-[40%] hover:opacity-70 hover:grayscale-0"
          : "bg-white border-[#d4dcc4] hover:border-[#6b7f4e]/40 hover:shadow-md"
      }`}
      onClick={!isGenerating ? onAction : undefined}
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div
          className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
            isLocked
              ? "bg-gray-100 text-gray-400"
              : "bg-[#6b7f4e]/8 text-[#6b7f4e] group-hover:bg-[#6b7f4e]/15"
          }`}
        >
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className={`font-semibold text-sm leading-tight ${isLocked ? "text-gray-400" : "text-[#2d3a1e]"}`}
            >
              {title}
            </h3>
            {isFree && (
              <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0">
                {t("sessions.kostenlos")}
              </span>
            )}
            {isLocked && (
              <Lock className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            )}
          </div>
          <p
            className={`text-xs leading-relaxed mb-2 ${isLocked ? "text-gray-400" : "text-[#5a6b4a]"}`}
          >
            {description}
          </p>
          <div className="flex items-center justify-between">
            <div
              className={`flex items-center gap-1.5 text-xs ${isLocked ? "text-gray-400" : "text-[#8a9680]"}`}
            >
              <Clock className="w-3 h-3" />
              <span>{duration}</span>
            </div>
            {isGenerating ? (
              <span className="flex items-center gap-1.5 text-xs text-[#6b7f4e]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {t("sessions.wirdErstellt")}
              </span>
            ) : isLocked ? (
              <span className="flex items-center gap-1 text-xs text-[#6b7f4e] opacity-0 group-hover:opacity-100 transition-opacity">
                {t("sessions.premiumFreischalten")}
                <ArrowRight className="w-3 h-3" />
              </span>
            ) : (
              <span className="flex items-center gap-1 text-xs text-[#6b7f4e] opacity-0 group-hover:opacity-100 transition-opacity">
                <Play className="w-3 h-3" />
                {t("sessions.starten")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Pricing Modal — Only place where prices are shown
   ═══════════════════════════════════════════════════════════ */
function PricingModal({
  onClose,
  onCheckout,
  isLoading,
}: {
  onClose: () => void;
  onCheckout: (planId: string) => void;
  isLoading: boolean;
}) {
  const { isDemo } = useDemo();
  const { t } = useI18n();

  // Im Demo-Modus: Statt Preise zeigen, zu /termin verweisen
  if (isDemo) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center relative"
          onClick={e => e.stopPropagation()}
        >
          <h3 className="text-xl font-bold text-[#2d3a1e] mb-3">
            {t("sessions.premiumSessions")}
          </h3>
          <p className="text-sm text-[#5a6b4a] mb-6">
            {t("sessions.demoBody")}
          </p>
          <a
            href="/termin"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#C9A96E] text-white font-medium hover:bg-[#b8953d] transition-colors"
          >
            {t("sessions.beratungBuchen")}
          </a>
          <button
            onClick={onClose}
            className="block mx-auto mt-4 text-sm text-gray-500 hover:text-gray-700"
          >
            {t("sessions.schliessen")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6 md:p-8 relative"
        onClick={e => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#6b7f4e]/10 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-6 h-6 text-[#6b7f4e]" />
          </div>
          <h2
            className="text-xl font-semibold text-[#2d3a1e]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("sessions.ayuPremium")}
          </h2>
          <p className="text-sm text-[#5a6b4a] mt-2 max-w-md mx-auto">
            {t("sessions.ayuPremiumDesc")}
          </p>
        </div>

        {/* 3 Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Starter */}
          <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5 flex flex-col">
            <div className="text-xs font-medium text-[#8a9680] uppercase tracking-wider mb-1">
              Starter
            </div>
            <h4 className="font-semibold text-[#2d3a1e] mb-1">AYU Audio S</h4>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-2xl font-bold text-[#2d3a1e]">9,90</span>
              <span className="text-sm text-[#5a6b4a]">€/Monat</span>
            </div>
            <ul className="space-y-2 mb-5 flex-1">
              {[
                "10 Sessions pro Monat",
                "Alle Übungen verfügbar",
                "Personalisierte Inhalte",
                "Jederzeit kündbar",
              ].map(f => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-xs text-[#5a6b4a]"
                >
                  <Check className="w-3.5 h-3.5 text-[#6b7f4e] flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onCheckout("starter")}
              disabled={isLoading}
              className="w-full py-2.5 rounded-xl text-sm font-medium border border-[#6b7f4e] text-[#6b7f4e] hover:bg-[#6b7f4e]/5 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                t("sessions.starterWaehlen")
              )}
            </button>
          </div>

          {/* Medium — Beliebt */}
          <div className="rounded-2xl border-2 border-[#6b7f4e] bg-white p-5 relative flex flex-col">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="bg-[#6b7f4e] text-white text-[10px] font-semibold px-3 py-1 rounded-full flex items-center gap-1">
                <Star className="w-3 h-3" /> {t("sessions.beliebt")}
              </span>
            </div>
            <div className="text-xs font-medium text-[#6b7f4e] uppercase tracking-wider mb-1">
              Medium
            </div>
            <h4 className="font-semibold text-[#2d3a1e] mb-1">AYU Audio M</h4>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-2xl font-bold text-[#2d3a1e]">19,90</span>
              <span className="text-sm text-[#5a6b4a]">€/Monat</span>
            </div>
            <ul className="space-y-2 mb-5 flex-1">
              {[
                "25 Sessions pro Monat",
                "Alle Übungen verfügbar",
                "Personalisierte Inhalte",
                "Ideal für tägliche Nutzung",
                "Jederzeit kündbar",
              ].map(f => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-xs text-[#5a6b4a]"
                >
                  <Check className="w-3.5 h-3.5 text-[#6b7f4e] flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onCheckout("medium")}
              disabled={isLoading}
              className="w-full py-2.5 rounded-xl text-sm font-medium bg-[#6b7f4e] text-white hover:bg-[#5a6d3e] transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                t("sessions.mediumWaehlen")
              )}
            </button>
          </div>

          {/* Unlimited */}
          <div className="rounded-2xl border border-[#d4dcc4] bg-white p-5 flex flex-col">
            <div className="text-xs font-medium text-[#8a9680] uppercase tracking-wider mb-1">
              Unlimited
            </div>
            <h4 className="font-semibold text-[#2d3a1e] mb-1">AYU Audio L</h4>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-2xl font-bold text-[#2d3a1e]">29,90</span>
              <span className="text-sm text-[#5a6b4a]">€/Monat</span>
            </div>
            <ul className="space-y-2 mb-5 flex-1">
              {[
                "Unbegrenzte Sessions",
                "Alle Übungen verfügbar",
                "Prioritäts-Support",
                "Exklusive Premium-Inhalte",
                "Jederzeit kündbar",
              ].map(f => (
                <li
                  key={f}
                  className="flex items-start gap-2 text-xs text-[#5a6b4a]"
                >
                  <Check className="w-3.5 h-3.5 text-[#6b7f4e] flex-shrink-0 mt-0.5" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => onCheckout("unlimited")}
              disabled={isLoading}
              className="w-full py-2.5 rounded-xl text-sm font-medium border border-[#6b7f4e] text-[#6b7f4e] hover:bg-[#6b7f4e]/5 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mx-auto" />
              ) : (
                t("sessions.unlimitedWaehlen")
              )}
            </button>
          </div>
        </div>

        <div className="bg-[#f0f4e8] rounded-xl p-4 text-center">
          <p className="text-sm font-medium text-[#2d3a1e] mb-1">
            {t("sessions.flexibel")}
          </p>
          <p className="text-xs text-[#6b7f4e]">
            {t("sessions.flexibelDesc")}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Audio Player (Bottom Bar)
   ═══════════════════════════════════════════════════════════ */
const SESSION_BG_DEFAULT =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/session-bg-zen_efaf3326.jpg";

/** Individuelle Hintergrundbilder pro Session-Typ */
const SESSION_BG_IMAGES: Record<string, string> = {
  // Basis-Sessions
  "morgen-meditation":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/0_FH3OvLOebuuKSCX8Jp6Mkm_1773243701771_na1fn_L2hvbWUvdWJ1bnR1L21vcmdlbi1tZWRpdGF0aW9u_295dc10a.png",
  "4-7-8-atem":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/1_5vYeFyBecJHzTi9cNewXNd_1773243696613_na1fn_L2hvbWUvdWJ1bnR1LzQtNy04LWJyZWF0aGluZw_b7d91868.png",
  "body-scan":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/2_ErSTWmL6MZ09flO2sv6GBO_1773243683431_na1fn_L2hvbWUvdWJ1bnR1L2JvZHktc2Nhbg_19a474cb.png",
  "abend-reflexion":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/3_IL8fVIreNebo4NK40tOAUS_1773243677572_na1fn_L2hvbWUvdWJ1bnR1L2FiZW5kLXJlZmxleGlvbg_2ddaf967.png",
  muskelentspannung:
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/4_Aj7GoQgvUJ12I2JLUb1TLt_1773243682800_na1fn_L2hvbWUvdWJ1bnR1L3Byb2dyZXNzaXZlLW11c2NsZS1yZWxheGF0aW9u_6561c8c8.png",
  // Personalisierte Sessions
  "p-nadi-shodhana":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/5_YQW4TMMrUq9geNCK7TajTa_1773243686073_na1fn_L2hvbWUvdWJ1bnR1L25hZGktc2hvZGhhbmE_0bacfd33.png",
  "p-bhramari":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/6_swBvq9MhHZ9yZojBoQtiKK_1773243666524_na1fn_L2hvbWUvdWJ1bnR1L2JocmFtYXJpLWh1bW1pbmctYmVlLWJyZWF0aA_64a997ef.png",
  "p-vagusnerv":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/7_LPc8bmfshMOfR7EV0XUwoR_1773243810886_na1fn_L2hvbWUvdWJ1bnR1L3ZhZ3VzLW5lcnYtc3RpbXVsYXRpb24_7192f8af.png",
  "p-breathwork":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/8_dYPiYWI7iYltrsM9qEV2tE_1773243702245_na1fn_L2hvbWUvdWJ1bnR1L2ludGVuc2l2ZS1icmVhdGh3b3Jr_5bbea232.png",
  "p-koerperreise":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/9_xEnXbSjebnWXtYJVy0IsDa_1773243804758_na1fn_L2hvbWUvdWJ1bnR1L2ltYWdpbmFyeV9ib2R5X2pvdXJuZXk_4ba73481.png",
  "p-achtsamkeit":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/10_lBp2nAAiAadV7JxaKmDFxx_1773243685230_na1fn_L2hvbWUvdWJ1bnR1L21pbmRmdWxuZXNzLW1lZGl0YXRpb24_732f182b.png",
  "p-dosha-balance":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/11_VZPXJH1jxACKtq584tIlFu_1773243683077_na1fn_L2hvbWUvdWJ1bnR1L3BoZW5vdHlwZS1iYWxhbmNlLW1lZGl0YXRpb24_e7625ee2.png",
  "p-yoga-nidra":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/12_uup3N6NsZoX1XCFsoHIzR1_1773243682495_na1fn_L2hvbWUvdWJ1bnR1L3lvZ2EtbmlkcmE_43eddd98.png",
  "p-chakra":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/13_p2vNIgjZFrmDSC6ozwO1XK_1773243677395_na1fn_L2hvbWUvdWJ1bnR1L2NoYWtyYV9tZWRpdGF0aW9u_13e48ecb.png",
  "p-abhyanga":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/14_UH21s3C5b5FIaeoJ9L9pKe_1773243813160_na1fn_L2hvbWUvdWJ1bnR1L2RhbmtiYXJrZWl0cy1tZWRpdGF0aW9u_115e8e20.png",
  "p-marma":
    "https://d2xsxph8kpxj0f.cloudfront.net/310519663397508885/Atzyym4VHHtDL2VhJSwxBc/15_NolNAJCjmJ8rYELhXoz7sN_1773243694743_na1fn_L2hvbWUvdWJ1bnR1L3Zpc3VhbGlzaWVydW5ncy1tZWRpdGF0aW9u_940b0eff.png",
};

function AudioPlayer({
  title,
  audioUrl,
  onClose,
  description,
  icon,
  introAudioUrl,
  mainAudioUrl,
  sessionId,
  clientId,
}: {
  title: string;
  audioUrl: string | null;
  onClose: () => void;
  description?: string;
  icon?: React.ReactNode;
  introAudioUrl?: string;
  mainAudioUrl?: string;
  sessionId?: string;
  clientId?: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [startTime] = useState(() => Date.now());
  const [sessionDuration, setSessionDuration] = useState(0);
  const [playingIntro, setPlayingIntro] = useState(!!introAudioUrl);
  const { t } = useI18n();
  const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2] as const;
  const [speedIndex, setSpeedIndex] = useState(1); // default 1x
  const playbackRate = SPEED_OPTIONS[speedIndex];

  const cycleSpeed = () => {
    const next = (speedIndex + 1) % SPEED_OPTIONS.length;
    setSpeedIndex(next);
    if (audioRef.current) {
      audioRef.current.playbackRate = SPEED_OPTIONS[next];
    }
  };
  const mainAudioUrlRef = useRef(mainAudioUrl);
  mainAudioUrlRef.current = mainAudioUrl;

  // Determine which URL to play first
  const initialUrl = introAudioUrl || audioUrl;

  useEffect(() => {
    if (!initialUrl) return;
    const audio = new Audio(initialUrl);
    audioRef.current = audio;
    if (introAudioUrl) setPlayingIntro(true);

    audio.addEventListener("loadedmetadata", () => {
      // Only show duration for main audio, not intro
      if (!introAudioUrl || !playingIntro) {
        setDuration(audio.duration);
      }
    });
    audio.addEventListener("timeupdate", () => {
      setCurrentTime(audio.currentTime);
      if (audio.duration > 0) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    });
    audio.addEventListener("ended", () => {
      // If intro just ended and we have a main audio, transition to it
      if (playingIntro && mainAudioUrlRef.current) {
        setPlayingIntro(false);
        setProgress(0);
        setCurrentTime(0);
        audio.src = mainAudioUrlRef.current;
        audio.load();
        audio
          .play()
          .then(() => {
            setIsPlaying(true);
            setDuration(audio.duration);
          })
          .catch(() => {});
        return;
      }
      // Main audio ended
      setIsPlaying(false);
      setIsEnded(true);
      setProgress(100);
      setSessionDuration(Math.floor((Date.now() - startTime) / 1000));
    });

    audio
      .play()
      .then(() => {
        setIsPlaying(true);
        setIsEnded(false);
      })
      .catch(() => {});

    return () => {
      audio.pause();
      audio.src = "";
    };
  }, [initialUrl]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isEnded) {
      // Restart from beginning
      audioRef.current.currentTime = 0;
      setIsEnded(false);
      setProgress(0);
      audioRef.current.play().then(() => setIsPlaying(true));
      return;
    }
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    audioRef.current.muted = !muted;
    setMuted(!muted);
  };

  const skipBack = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(
      0,
      audioRef.current.currentTime - 15
    );
  };

  const skipForward = () => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.min(
      duration,
      audioRef.current.currentTime + 15
    );
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * duration;
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    if (m === 0) return `${sec} ${t("sessions.sek")}`;
    return sec > 0 ? `${m} ${t("sessions.min")} ${sec} ${t("sessions.sek")}` : `${m} ${t("sessions.min")}`;
  };

  // Session-Beendet screen
  if (isEnded) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col">
        {/* Background image with overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${(sessionId && SESSION_BG_IMAGES[sessionId]) || SESSION_BG_DEFAULT})`,
          }}
        />
        <div className="absolute inset-0 bg-[#2d3a1e]/70 backdrop-blur-sm" />

        {/* Content */}
        <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
          <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center text-white mb-6">
            <CheckCircle2 className="w-8 h-8" />
          </div>

          <h2
            className="text-2xl sm:text-3xl font-bold text-white text-center mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("sessions.sessionBeendet")}
          </h2>
          <p className="text-white/70 text-sm text-center mb-2">{title}</p>
          <p className="text-white/50 text-xs text-center mb-4">
            {t("sessions.dauer")} {formatDuration(sessionDuration)}
          </p>

          {/* Feedback — Daumen hoch/runter */}
          {clientId && (
            <div className="w-full max-w-sm mb-6">
              <ThumbsFeedback
                clientId={clientId}
                sessionType="session"
                sessionRef={sessionId}
                context="/app/sessions"
                variant="card"
                label="Wie war die Session?"
                dark
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                if (audioRef.current) {
                  audioRef.current.currentTime = 0;
                  setIsEnded(false);
                  setProgress(0);
                  audioRef.current.play().then(() => setIsPlaying(true));
                }
              }}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/15 backdrop-blur-md text-white text-sm font-medium hover:bg-white/25 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              {t("sessions.nochmalStarten")}
            </button>
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#6b7f4e] text-white text-sm font-medium hover:bg-[#5a6d3e] transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              {t("sessions.zurueckDashboard")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fullscreen player
  return (
    <div className="fixed inset-0 z-50 flex flex-col">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url(${(sessionId && SESSION_BG_IMAGES[sessionId]) || SESSION_BG_DEFAULT})`,
        }}
      />
      <div className="absolute inset-0 bg-[#2d3a1e]/60 backdrop-blur-sm" />

      {/* Top bar */}
      <div className="relative z-10 px-6 py-4 flex items-center justify-between">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-sm text-white/70 hover:text-white transition-colors"
        >
          <ChevronDown className="w-5 h-5" />
          <span className="hidden sm:inline">{t("sessions.zurück")}</span>
        </button>
        <div className="flex items-center gap-2">
          {/* Speed control */}
          <button
            onClick={cycleSpeed}
            className="h-9 px-3 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors text-xs font-bold"
          >
            {playbackRate}x
          </button>
          <button
            onClick={toggleMute}
            className="w-9 h-9 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors"
          >
            {muted ? (
              <VolumeX className="w-4 h-4" />
            ) : (
              <Volume2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Center content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6">
        {/* Session icon */}
        {icon && (
          <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-white/15 backdrop-blur-md flex items-center justify-center text-white mb-6 shadow-2xl">
            <div className="scale-150">{icon}</div>
          </div>
        )}

        {/* Title */}
        <h2
          className="text-2xl sm:text-3xl font-bold text-white text-center mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {title}
        </h2>
        {description && (
          <p className="text-white/60 text-sm text-center mb-8 max-w-md">
            {description}
          </p>
        )}

        {/* Playback controls */}
        <div className="flex items-center gap-6 mb-8">
          <button
            onClick={skipBack}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors"
          >
            <SkipBack className="w-4 h-4" />
          </button>

          <button
            onClick={togglePlay}
            className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white hover:bg-white/30 transition-all shadow-2xl"
          >
            {isPlaying ? (
              <Pause className="w-7 h-7 sm:w-8 sm:h-8" />
            ) : (
              <Play className="w-7 h-7 sm:w-8 sm:h-8 ml-1" />
            )}
          </button>

          <button
            onClick={skipForward}
            className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 transition-colors"
          >
            <SkipForward className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Bottom progress bar */}
      <div className="relative z-10 px-6 pb-8 sm:pb-10">
        <div className="max-w-lg mx-auto">
          {/* Progress bar */}
          <div
            className="w-full h-1.5 bg-white/20 rounded-full overflow-hidden cursor-pointer mb-2"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-white/80 rounded-full transition-all duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          {/* Time */}
          <div className="flex items-center justify-between">
            <span className="text-xs text-white/50">
              {formatTime(currentTime)}
            </span>
            <span className="text-xs text-white/50">
              {duration > 0 ? formatTime(duration) : "--:--"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Session Generation Overlay (MENTO-Style)
   ═══════════════════════════════════════════════════════════ */

function SessionGenerationOverlay({
  session,
  onBack,
  onComplete,
}: {
  session: PersonalizedSession;
  onBack: () => void;
  onComplete: (audioUrl: string, introAudioUrl?: string) => void;
}) {
  const { client } = useClientAuth();
  const { t } = useI18n();
  const [phase, setPhase] = useState<
    "checking" | "text" | "audio" | "fertig" | "error"
  >("checking");
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const startSession = trpc.guidedSessions.start.useMutation();
  const startedRef = useRef(false);
  const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Simulate progress animation for first-time generation
  const startProgressAnimation = (fromPhase: "text" | "audio") => {
    if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
    const startPct = fromPhase === "text" ? 0 : 50;
    const endPct = fromPhase === "text" ? 45 : 90;
    let current = startPct;
    setProgress(startPct);
    setPhase(fromPhase);
    progressIntervalRef.current = setInterval(() => {
      current += Math.random() * 2 + 0.5;
      if (current >= endPct) {
        current = endPct;
        if (progressIntervalRef.current)
          clearInterval(progressIntervalRef.current);
      }
      setProgress(Math.min(current, endPct));
    }, 500);
  };

  useEffect(() => {
    return () => {
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Start session (cache-first)
  useEffect(() => {
    if (startedRef.current || !client) return;
    startedRef.current = true;

    const start = async () => {
      try {
        // Start with "checking cache" phase
        setPhase("checking");
        setProgress(0);

        // Start the progress animation for text generation
        // The backend handles both text + audio, so we simulate the phases
        startProgressAnimation("text");

        // After ~8 sec, switch to audio phase animation
        const audioPhaseTimer = setTimeout(() => {
          if (progressIntervalRef.current)
            clearInterval(progressIntervalRef.current);
          startProgressAnimation("audio");
        }, 8000);

        const result = await startSession.mutateAsync({
          clientId: client.clientId,
          type: session.type as any,
          durationMinutes: 5,
          voicePreference: "annam",
        });

        clearTimeout(audioPhaseTimer);
        if (progressIntervalRef.current)
          clearInterval(progressIntervalRef.current);

        if (result.success && result.audioUrl) {
          // Complete!
          setProgress(100);
          setPhase("fertig");
          setTimeout(
            () =>
              onComplete(result.audioUrl!, result.introAudioUrl ?? undefined),
            result.wasCached ? 600 : 1200
          );
        } else {
          setError(
            (result as any).error || "Session konnte nicht gestartet werden."
          );
          setPhase("error");
        }
      } catch (e: any) {
        if (progressIntervalRef.current)
          clearInterval(progressIntervalRef.current);
        setError(e.message || "Fehler bei der Session.");
        setPhase("error");
      }
    };

    start();
  }, [client]);

  const steps = [
    {
      key: "text",
      label: t("sessions.stepText"),
      done: phase === "audio" || phase === "fertig",
    },
    { key: "audio", label: t("sessions.stepAudio"), done: phase === "fertig" },
    { key: "fertig", label: t("sessions.stepFertig"), done: phase === "fertig" },
  ];

  const phaseLabel =
    phase === "checking"
      ? t("sessions.phaseChecking")
      : phase === "text"
        ? t("sessions.phaseText")
        : phase === "audio"
          ? t("sessions.phaseAudio")
          : phase === "fertig"
            ? t("sessions.phaseFertig")
            : t("sessions.phaseFehler");

  const phaseDescription =
    phase === "checking"
      ? t("sessions.phaseCheckingDesc")
      : phase === "text"
        ? t("sessions.phaseTextDesc")
        : phase === "audio"
          ? t("sessions.phaseAudioDesc")
          : phase === "fertig"
            ? t("sessions.phaseFertigDesc")
            : "";

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "#f8f8f8" }}
    >
      {/* Back button */}
      <div className="px-6 py-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[#8a9680] hover:text-[#5a6b4a] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("sessions.zurueckDashboard")}
        </button>
      </div>

      {/* Centered content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 -mt-12">
        {/* Session icon */}
        <div className="w-16 h-16 rounded-2xl bg-[#6b7f4e]/8 flex items-center justify-center text-[#8b6914] mb-5">
          {session.icon}
        </div>

        {/* Session info */}
        <h1
          className="text-2xl font-bold text-[#2d3a1e] text-center mb-2"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {session.title}
        </h1>
        <p className="text-sm text-[#8a9680] text-center mb-1">
          {session.description}
        </p>
        <p className="text-xs text-[#6b7f4e] font-medium text-center mb-8">
          {t("sessions.personalisiertePremium")}
        </p>

        {/* Status card */}
        {phase === "error" ? (
          <div className="bg-white rounded-2xl shadow-lg border border-red-100 p-6 w-full max-w-md text-center">
            <p className="text-sm font-medium text-red-600 mb-2">
              {t("sessions.fehlerErstellung")}
            </p>
            <p className="text-xs text-red-500 mb-4">{error}</p>
            <button
              onClick={onBack}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-[#6b7f4e] text-white hover:bg-[#5a6d3e] transition-colors"
            >
              {t("sessions.zurück")}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-[#e8e3d9] p-6 w-full max-w-md">
            {/* Phase label */}
            <h3 className="text-base font-semibold text-[#2d3a1e] text-center mb-1">
              {phaseLabel}
            </h3>
            <p className="text-xs text-[#8a9680] text-center mb-4">
              {phaseDescription}
            </p>

            {/* Progress bar */}
            <div className="w-full h-2 bg-[#e8e3d9] rounded-full overflow-hidden mb-2">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${progress}%`,
                  background: "linear-gradient(90deg, #6b7f4e, #8b6914)",
                }}
              />
            </div>

            {/* Progress percentage + time estimate */}
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs text-[#8a9680]">
                {Math.round(progress)}%
              </span>
              {phase !== "fertig" && (
                <span className="text-xs text-[#8a9680] flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {t("sessions.approxTime")}
                </span>
              )}
            </div>

            {/* Step indicators */}
            <div className="flex items-center justify-center gap-0">
              {steps.map((step, i) => (
                <div key={step.key} className="flex items-center">
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                        step.done
                          ? "bg-[#6b7f4e] text-white"
                          : phase === step.key
                            ? "bg-white border-2 border-[#6b7f4e] text-[#6b7f4e]"
                            : "bg-[#e8e3d9] text-[#8a9680]"
                      }`}
                    >
                      {step.done ? <Check className="w-3 h-3" /> : null}
                    </div>
                    <span
                      className={`text-xs font-medium ${
                        step.done
                          ? "text-[#6b7f4e]"
                          : phase === step.key
                            ? "text-[#2d3a1e]"
                            : "text-[#8a9680]"
                      }`}
                    >
                      {step.label}
                    </span>
                  </div>
                  {i < steps.length - 1 && (
                    <div
                      className={`w-8 h-0.5 mx-2 ${
                        step.done ? "bg-[#6b7f4e]" : "bg-[#e8e3d9]"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Explanation text */}
        {phase !== "error" && phase !== "fertig" && (
          <div className="mt-8 text-center max-w-sm">
            <h4 className="text-sm font-semibold text-[#2d3a1e] mb-2">
              {t("sessions.wasPassiert")}
            </h4>
            <p className="text-xs text-[#8a9680] leading-relaxed">
              {t("sessions.wasPassiertText", { title: session.title })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════ */
export default function AppSessions() {
  const { client } = useClientAuth();
  const { t } = useI18n();
  const [, setLocation] = useLocation();
  const [filter, setFilter] = useState<CategoryFilter>("all");
  const [showPricing, setShowPricing] = useState(false);
  const [playingTitle, setPlayingTitle] = useState<string | null>(null);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const [playingDescription, setPlayingDescription] = useState<
    string | undefined
  >(undefined);
  const [playingIcon, setPlayingIcon] = useState<React.ReactNode | undefined>(
    undefined
  );
  const [generatingSessionId, setGeneratingSessionId] = useState<string | null>(
    null
  );
  const [generatingSession, setGeneratingSession] =
    useState<PersonalizedSession | null>(null);
  const [playingIntroUrl, setPlayingIntroUrl] = useState<string | undefined>(
    undefined
  );
  const [playingMainUrl, setPlayingMainUrl] = useState<string | undefined>(
    undefined
  );
  const [playingSessionId, setPlayingSessionId] = useState<string | undefined>(
    undefined
  );

  // Stripe queries
  const { data: premiumStatus, refetch: refetchPremium } =
    trpc.stripe.getPremiumStatus.useQuery(
      { clientId: client?.clientId ?? "" },
      { enabled: !!client?.clientId }
    );

  const createCheckout = trpc.stripe.createCheckout.useMutation();
  const useSessionMutation = trpc.stripe.useSession.useMutation();
  const markFreeUsed = trpc.stripe.markFreeSessionUsed.useMutation();
  const generateSession = trpc.guidedSessions.generate.useMutation();

  const hasPremium = premiumStatus?.hasAccess ?? false;
  const currentPlan = premiumStatus?.plan ?? null;
  const sessionsUsed = premiumStatus?.sessionsUsed ?? 0;
  const sessionsLimit = premiumStatus?.sessionsLimit ?? null;
  const purchasedSessions = useMemo(
    () => new Set(premiumStatus?.purchasedSessions ?? []),
    [premiumStatus?.purchasedSessions]
  );
  const usedFreeSessions = useMemo(
    () => new Set(premiumStatus?.usedFreeSessions ?? []),
    [premiumStatus?.usedFreeSessions]
  );

  // Checkout result — retry premium status multiple times (webhook may take a few seconds)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (checkout === "success") {
      toast.success(t("sessions.zahlung"), {
        description: t("sessions.zahlungDesc"),
        duration: 10000,
      });
      // Retry premium status check multiple times over 30 seconds
      const retryDelays = [1000, 3000, 5000, 8000, 12000, 18000, 25000];
      let resolved = false;
      retryDelays.forEach(delay => {
        setTimeout(async () => {
          if (resolved) return;
          const result = await refetchPremium();
          if (result.data?.hasAccess) {
            resolved = true;
            toast.success(t("sessions.freigeschaltet"), {
              description: t("sessions.freigeschaltetDesc"),
            });
          }
        }, delay);
      });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (checkout === "cancel") {
      toast(t("sessions.checkoutAbgebrochen"), {
        description: t("sessions.checkoutAbgebrochenDesc"),
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const limitReached = sessionsLimit !== null && sessionsUsed >= sessionsLimit;

  const canAccessPersonalized = (sessionId: string): boolean => {
    // If user has a subscription but limit is reached, they can't access
    if (hasPremium && currentPlan && currentPlan !== "einzelsession") {
      if (limitReached) return false;
      return true;
    }
    return purchasedSessions.has(sessionId);
  };

  const handleCheckout = async (planId: string) => {
    if (!client) {
      toast.error(t("sessions.bitteMelden"));
      return;
    }
    try {
      const result = await createCheckout.mutateAsync({
        planId,
        clientId: client.clientId,
        clientEmail: client.email,
        origin: window.location.origin,
      });
      if (result.url) {
        toast(t("sessions.weiterleitungStripe"));
        window.open(result.url, "_blank");
      }
    } catch (e: any) {
      toast.error(t("sessions.checkoutFehler"), { description: e.message });
    }
  };

  const handlePlayBasis = (session: BasisSession) => {
    setPlayingTitle(session.title);
    setPlayingAudioUrl(session.audioUrl);
    setPlayingDescription(session.description);
    setPlayingIcon(session.icon);
    setPlayingIntroUrl(undefined);
    setPlayingMainUrl(undefined);
    setPlayingSessionId(session.id);
    toast.success(`${session.title}`, {
      description: t("sessions.sessionGestartetDesc"),
    });
  };

  /**
   * Unified session access check for Live-Gespräche and Chat-Übungen.
   * - Premium users: count against subscription limit (useSession mutation)
   * - Non-premium: allow 1 free session per type, block after that
   */
  const handleLiveChatAccess = async (
    sessionId: string,
    isFree: boolean,
    sessionType: "live" | "chat",
    onAllowed: () => void
  ) => {
    if (!client) {
      toast.error(t("sessions.bitteMelden"));
      return;
    }

    // Premium user: check and decrement session limit
    if (hasPremium && currentPlan && currentPlan !== "einzelsession") {
      // Check limit before starting
      if (sessionsLimit !== null && sessionsUsed >= sessionsLimit) {
        toast.error(t("sessions.sessionLimitErreicht"), {
          description: t("sessions.limitUpgradeDesc"),
        });
        setShowPricing(true);
        return;
      }
      try {
        const result = await useSessionMutation.mutateAsync({
          clientId: client.clientId,
        });
        if (!result.success) {
          toast.error(t("sessions.sessionLimitErreicht"), {
            description: t("sessions.limitUpgradeDesc"),
          });
          setShowPricing(true);
          return;
        }
        refetchPremium();
      } catch {
        // Continue even if tracking fails
      }
      onAllowed();
      return;
    }

    // Non-premium: check free session
    if (isFree) {
      if (usedFreeSessions.has(sessionId)) {
        // Already used the free session
        toast(t("sessions.gratisGenutzt"), {
          description: t("sessions.gratisGenutztDesc"),
        });
        setShowPricing(true);
        return;
      }
      // Mark as used
      try {
        await markFreeUsed.mutateAsync({
          clientId: client.clientId,
          sessionId,
          sessionType,
        });
        refetchPremium();
      } catch {
        // Continue even if marking fails
      }
      onAllowed();
      return;
    }

    // Non-premium, non-free session
    setShowPricing(true);
  };

  const handlePersonalizedAction = async (session: PersonalizedSession) => {
    const accessible = canAccessPersonalized(session.id);

    if (!accessible) {
      setShowPricing(true);
      return;
    }

    if (!client) {
      toast.error(t("sessions.bitteMelden"));
      return;
    }

    // Track session usage for subscription users
    if (hasPremium && currentPlan && currentPlan !== "einzelsession") {
      // Check limit before starting
      if (sessionsLimit !== null && sessionsUsed >= sessionsLimit) {
        toast.error(t("sessions.sessionLimitErreicht"), {
          description: t("sessions.limitUpgradeDesc"),
        });
        setShowPricing(true);
        return;
      }
      try {
        const result = await useSessionMutation.mutateAsync({
          clientId: client.clientId,
        });
        if (!result.success) {
          toast.error(t("sessions.sessionLimitErreicht"), {
            description: t("sessions.limitUpgradeDesc"),
          });
          setShowPricing(true);
          return;
        }
        refetchPremium();
      } catch {
        // Continue even if tracking fails
      }
    }

    // Open the generation overlay instead of inline generation
    setGeneratingSession(session);
  };

  // Filtering
  const filteredBasis =
    filter === "all" || filter === "basis"
      ? BASIS_SESSIONS
      : BASIS_SESSIONS.filter(s => s.category === filter);

  const filteredPersonalized =
    filter === "all"
      ? PERSONALIZED_SESSIONS
      : filter === "basis" || filter === "live" || filter === "chat"
        ? []
        : PERSONALIZED_SESSIONS.filter(s => s.category === filter);

  const filteredLive =
    filter === "all" || filter === "live" ? LIVE_SESSIONS : [];
  const filteredChat =
    filter === "all" || filter === "chat" ? CHAT_EXERCISES : [];

  // If generating, show the overlay
  if (generatingSession) {
    return (
      <SessionGenerationOverlay
        session={generatingSession}
        onBack={() => setGeneratingSession(null)}
        onComplete={(audioUrl, introAudioUrl) => {
          // Set description and icon for fullscreen player
          setPlayingDescription(generatingSession.description);
          setPlayingIcon(generatingSession.icon);
          setPlayingTitle(generatingSession.title);
          setPlayingSessionId(generatingSession.id);
          // Store both URLs — AudioPlayer handles intro→main transition
          if (introAudioUrl) {
            setPlayingIntroUrl(introAudioUrl);
            setPlayingMainUrl(audioUrl);
            setPlayingAudioUrl(introAudioUrl); // Start with intro
          } else {
            setPlayingIntroUrl(undefined);
            setPlayingMainUrl(undefined);
            setPlayingAudioUrl(audioUrl);
          }
          setGeneratingSession(null);
          toast.success(t("sessions.sessionBereit"), {
            description: t("sessions.sessionBereitDesc"),
          });
        }}
      />
    );
  }

  return (
    <div
      className="h-full flex flex-col overflow-hidden"
      style={{ background: "#f8f8f8" }}
    >
      {/* Header */}
      <div className="px-6 py-5 border-b border-[#d4dcc4]">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-0.5">
              <h1
                className="text-xl font-semibold text-[#2d3a1e]"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("sessions.uebungenSessions")}
              </h1>
              {/* Session count badge — directly next to title */}
              {hasPremium && currentPlan ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#6b7f4e]/10 border border-[#6b7f4e]/20 flex-shrink-0">
                  <Crown className="w-3.5 h-3.5 text-[#6b7f4e]" />
                  <span className="text-xs font-medium text-[#6b7f4e]">
                    {currentPlan === "unlimited"
                      ? t("sessions.unbegrenzt")
                      : `${sessionsUsed}/${sessionsLimit ?? "?"} ${t("sessions.sessionCount")}`}
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => setShowPricing(true)}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#6b7f4e] text-white hover:bg-[#5a6d3e] transition-colors flex-shrink-0"
                >
                  <Crown className="w-3.5 h-3.5" />
                  Premium
                </button>
              )}
            </div>
            <p className="text-sm text-[#5a6b4a]">
              {t("sessions.headerDesc")}
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="relative border-b border-[#d4dcc4]">
        <div
          className="flex gap-2 overflow-x-auto scrollbar-hide px-3 sm:px-6 py-3"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {CATEGORIES.map(cat => (
            <button
              key={cat.key}
              onClick={() => setFilter(cat.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap shrink-0 ${
                filter === cat.key
                  ? "bg-[#6b7f4e] text-white"
                  : "bg-white text-[#5a6b4a] border border-[#d4dcc4] hover:border-[#6b7f4e]/20"
              }`}
            >
              {cat.icon}
              {t(cat.labelKey as any)}
            </button>
          ))}
          {/* right spacer so last pill isn't clipped */}
          <div className="shrink-0 w-3" />
        </div>
        {/* fade hint — more content to scroll */}
        <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent" />
      </div>

      {/* Content */}
      <div
        className="flex-1 overflow-y-auto overflow-x-hidden p-6"
        style={{ paddingBottom: playingTitle ? "5rem" : undefined }}
      >
        <div className="max-w-5xl mx-auto space-y-8">
          {/* Package Info Banner (for non-premium users) */}
          {!hasPremium && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#eef2e6] border border-[#d4dcc4]">
              <Info className="w-5 h-5 text-[#6b7f4e] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-[#2d3a1e] font-medium mb-1">
                  {t("sessions.basisKostenlos")}
                </p>
                <p className="text-xs text-[#5a6b4a] leading-relaxed">
                  {t("sessions.basisKostenlosDesc")}
                </p>
              </div>
            </div>
          )}

          {/* Basis Sessions */}
          {filteredBasis.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Headphones className="w-4 h-4 text-[#6b7f4e]" />
                <h2 className="text-sm font-semibold text-[#2d3a1e]">
                  {t("sessions.basisSessions")}
                </h2>
                <span className="text-xs text-[#8a9680]">
                  {filteredBasis.length} {t("sessions.uebungenLabel")}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredBasis.map(session => (
                  <SessionCard
                    key={session.id}
                    title={session.title}
                    description={session.description}
                    duration={session.duration}
                    icon={session.icon}
                    isFree={true}
                    isLocked={false}
                    onAction={() => handlePlayBasis(session)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Session Limit Reached Banner */}
          {hasPremium &&
            currentPlan &&
            currentPlan !== "unlimited" &&
            currentPlan !== "einzelsession" &&
            sessionsLimit !== null &&
            sessionsUsed >= sessionsLimit && (
              <div className="flex items-start gap-3 p-5 rounded-2xl bg-gradient-to-r from-[#8b6914]/10 to-[#6b7f4e]/10 border border-[#8b6914]/20">
                <div className="w-10 h-10 rounded-xl bg-[#8b6914]/15 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-5 h-5 text-[#8b6914]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-[#2d3a1e] mb-1">
                    {t("sessions.limitErreicht")} ({sessionsUsed}/{sessionsLimit})
                  </h3>
                  <p className="text-xs text-[#5a6b4a] leading-relaxed mb-3">
                    {t("sessions.limitErreichtDesc")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setShowPricing(true)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-medium bg-[#6b7f4e] text-white hover:bg-[#5a6d3e] transition-colors"
                    >
                      <Crown className="w-3.5 h-3.5" />
                      {t("sessions.paketUpgraden")}
                    </button>
                  </div>
                </div>
              </div>
            )}

          {/* Personalized Sessions */}
          {filteredPersonalized.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Crown className="w-4 h-4 text-[#6b7f4e]" />
                <h2 className="text-sm font-semibold text-[#2d3a1e]">
                  {t("sessions.personalisierteSessions")}
                </h2>
                <span className="text-xs text-[#8a9680]">
                  {filteredPersonalized.length} {t("sessions.uebungenLabel")}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredPersonalized.map(session => (
                  <SessionCard
                    key={session.id}
                    title={session.title}
                    description={session.description}
                    duration={session.duration}
                    icon={session.icon}
                    isFree={false}
                    isLocked={!canAccessPersonalized(session.id)}
                    isGenerating={generatingSessionId === session.id}
                    onAction={() => handlePersonalizedAction(session)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ═══ Live-Gespräche Section ═══ */}
          {filteredLive.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#6b7f4e]" />
                  <h2
                    className="text-base font-semibold text-[#2d3a1e]"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {t("sessions.liveGespraeche")}
                  </h2>
                </div>
                <span className="text-xs text-[#8a9680] font-medium">
                  {filteredLive.length} {t("sessions.gespraeche")}
                </span>
              </div>
              <p className="text-xs text-[#8a9680] mb-4 -mt-2">
                {t("sessions.liveDesc")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredLive.map(session => {
                  const isLocked =
                    !session.isFree && (!hasPremium || limitReached);
                  const freeUsed =
                    session.isFree && usedFreeSessions.has(session.id);
                  return (
                    <button
                      key={session.id}
                      onClick={() => {
                        if (isLocked) {
                          setShowPricing(true);
                          return;
                        }
                        // Enforce session limit for premium + free session tracking
                        handleLiveChatAccess(
                          session.id,
                          session.isFree,
                          "live",
                          () => {
                            const params = new URLSearchParams();
                            params.set("mode", "voice");
                            params.set("context", session.contextPrompt);
                            params.set("title", session.title);
                            params.set("sessionId", session.id);
                            setLocation(`/ayu-coach?${params.toString()}`);
                          }
                        );
                      }}
                      className={`group relative flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
                        isLocked
                          ? "bg-[#f0ede5]/60 border-[#e0dbd0] opacity-55 grayscale-[40%] hover:opacity-70 hover:grayscale-0 cursor-pointer"
                          : "bg-white border-[#d4dcc4] hover:border-[#6b7f4e]/40 hover:shadow-md cursor-pointer"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          isLocked
                            ? "bg-[#f0ede5] text-[#c4bfb4]"
                            : "bg-[#6b7f4e]/8 text-[#6b7f4e]"
                        }`}
                      >
                        {session.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-sm font-semibold ${
                              isLocked ? "text-[#8a9680]" : "text-[#2d3a1e]"
                            }`}
                          >
                            {session.title}
                          </span>
                          {isLocked && (
                            <Lock className="w-3.5 h-3.5 text-[#c4bfb4]" />
                          )}
                          {session.isFree && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                freeUsed
                                  ? "bg-gray-100 text-gray-500"
                                  : "bg-[#6b7f4e]/10 text-[#6b7f4e]"
                              }`}
                            >
                              {freeUsed ? t("sessions.genutzt") : t("sessions.gratis")}
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-xs mt-0.5 ${
                            isLocked ? "text-[#c4bfb4]" : "text-[#8a9680]"
                          }`}
                        >
                          {session.description}
                        </p>
                        <div className="flex items-center gap-1 mt-1.5">
                          <Phone className="w-3 h-3 text-[#6b7f4e]" />
                          <span className="text-[11px] text-[#8a9680]">
                            {session.duration}
                          </span>
                        </div>
                      </div>
                      {!isLocked && (
                        <span className="text-xs text-[#6b7f4e] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          {t("sessions.starten")} →
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ Chat-Übungen Section ═══ */}
          {filteredChat.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#6b7f4e]" />
                  <h2
                    className="text-base font-semibold text-[#2d3a1e]"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {t("sessions.chatUebungen")}
                  </h2>
                </div>
                <span className="text-xs text-[#8a9680] font-medium">
                  {filteredChat.length} {t("sessions.uebungenLabel")}
                </span>
              </div>
              <p className="text-xs text-[#8a9680] mb-4 -mt-2">
                {t("sessions.chatDesc")}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredChat.map(exercise => {
                  const isLocked =
                    !exercise.isFree && (!hasPremium || limitReached);
                  const freeUsed =
                    exercise.isFree && usedFreeSessions.has(exercise.id);
                  return (
                    <button
                      key={exercise.id}
                      onClick={() => {
                        if (isLocked) {
                          setShowPricing(true);
                          return;
                        }
                        // Enforce session limit for premium + free session tracking
                        handleLiveChatAccess(
                          exercise.id,
                          exercise.isFree,
                          "chat",
                          () => {
                            const params = new URLSearchParams();
                            params.set("mode", "chat");
                            params.set("context", exercise.contextPrompt);
                            params.set("title", exercise.title);
                            params.set("sessionId", exercise.id);
                            setLocation(`/ayu-coach?${params.toString()}`);
                          }
                        );
                      }}
                      className={`group relative flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
                        isLocked
                          ? "bg-[#f0ede5]/60 border-[#e0dbd0] opacity-55 grayscale-[40%] hover:opacity-70 hover:grayscale-0 cursor-pointer"
                          : "bg-white border-[#d4dcc4] hover:border-[#6b7f4e]/40 hover:shadow-md cursor-pointer"
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          isLocked
                            ? "bg-[#f0ede5] text-[#c4bfb4]"
                            : "bg-[#6b7f4e]/8 text-[#6b7f4e]"
                        }`}
                      >
                        {exercise.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-sm font-semibold ${
                              isLocked ? "text-[#8a9680]" : "text-[#2d3a1e]"
                            }`}
                          >
                            {exercise.title}
                          </span>
                          {isLocked && (
                            <Lock className="w-3.5 h-3.5 text-[#c4bfb4]" />
                          )}
                          {exercise.isFree && (
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                                freeUsed
                                  ? "bg-gray-100 text-gray-500"
                                  : "bg-[#6b7f4e]/10 text-[#6b7f4e]"
                              }`}
                            >
                              {freeUsed ? t("sessions.genutzt") : t("sessions.gratis")}
                            </span>
                          )}
                        </div>
                        <p
                          className={`text-xs mt-0.5 ${
                            isLocked ? "text-[#c4bfb4]" : "text-[#8a9680]"
                          }`}
                        >
                          {exercise.description}
                        </p>
                        <div className="flex items-center gap-1 mt-1.5">
                          <MessageSquare className="w-3 h-3 text-[#6b7f4e]" />
                          <span className="text-[11px] text-[#8a9680]">
                            {exercise.duration}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty state */}
          {filteredBasis.length === 0 &&
            filteredPersonalized.length === 0 &&
            filteredLive.length === 0 &&
            filteredChat.length === 0 && (
              <div className="text-center py-12">
                <Headphones className="w-10 h-10 text-[#d4dcc4] mx-auto mb-3" />
                <p className="text-sm text-[#8a9680]">
                  {t("sessions.keineKategorie")}
                </p>
              </div>
            )}

          {/* Bottom CTA */}
          {!hasPremium && (
            <div className="rounded-2xl bg-gradient-to-br from-[#6b7f4e]/10 to-[#6b7c3e]/5 border border-[#d4dcc4] p-6 text-center">
              <Crown className="w-6 h-6 text-[#6b7f4e] mx-auto mb-3" />
              <h3
                className="text-lg font-semibold text-[#2d3a1e] mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {t("sessions.alleFreischalten")}
              </h3>
              <p className="text-sm text-[#5a6b4a] max-w-md mx-auto leading-relaxed mb-4">
                {t("sessions.paketeDesc")}
              </p>
              <button
                onClick={() => setShowPricing(true)}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium bg-[#6b7f4e] text-white hover:bg-[#5a6d3e] transition-colors"
              >
                {t("sessions.paketeAnsehen")}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Modal */}
      {showPricing && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onCheckout={planId => handleCheckout(planId)}
          isLoading={createCheckout.isPending}
        />
      )}

      {/* Audio Player — Fullscreen */}
      {playingTitle && (
        <AudioPlayer
          title={playingTitle}
          audioUrl={playingAudioUrl}
          description={playingDescription}
          icon={playingIcon}
          introAudioUrl={playingIntroUrl}
          mainAudioUrl={playingMainUrl}
          sessionId={playingSessionId}
          clientId={client?.clientId}
          onClose={() => {
            setPlayingTitle(null);
            setPlayingAudioUrl(null);
            setPlayingDescription(undefined);
            setPlayingIcon(undefined);
            setPlayingIntroUrl(undefined);
            setPlayingMainUrl(undefined);
            setPlayingSessionId(undefined);
          }}
        />
      )}
    </div>
  );
}
